import React from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  IconButton,
  Button,
  alpha,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  Save,
  Edit,
  Add,
  CleaningServices,
  DeleteOutline,
  CheckCircleOutline,
} from "@mui/icons-material";

const TankCleaningTable = ({ cleaningList, setCleaningList }) => {
  const theme = useTheme();

  const handleTextChange = (index, newText) => {
    const updated = [...cleaningList];
    updated[index].text = newText;
    setCleaningList(updated);
  };

  const handleSave = (index) => {
    const updated = [...cleaningList];
    updated[index].editable = false;
    setCleaningList(updated);
  };

  const handleEdit = (index) => {
    const updated = [...cleaningList];
    updated[index].editable = true;
    setCleaningList(updated);
  };

  const handleRemove = (index) => {
    const updated = cleaningList.filter((_, i) => i !== index);
    setCleaningList(updated.length ? updated : [{ text: "", editable: true }]);
  };

  const handleAddRow = () => {
    setCleaningList([...cleaningList, { text: "", editable: true }]);
  };

  return (
    <Box sx={{ py: 1 }}>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: "secondary.main",
              display: "flex",
            }}
          >
            <CleaningServices sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            variant="subtitle2"
            fontWeight="900"
            sx={{ letterSpacing: 0.5 }}
          >
            CLEANING PROCEDURES
          </Typography>
        </Stack>

        {/* Cleaning List Items */}
        <Stack spacing={1}>
          {cleaningList.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: item.editable ? "primary.main" : "divider",
                bgcolor: item.editable
                  ? alpha(theme.palette.primary.main, 0.02)
                  : "transparent",
                transition: "0.2s",
              }}
            >
              {item.editable ? (
                <TextField
                  value={item.text}
                  onChange={(e) => handleTextChange(index, e.target.value)}
                  fullWidth
                  variant="standard"
                  placeholder="Enter cleaning step..."
                  autoFocus
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontWeight: 600 },
                  }}
                />
              ) : (
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ flexGrow: 1 }}
                >
                  <CheckCircleOutline color="action" sx={{ fontSize: 18 }} />
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    color="text.primary"
                  >
                    {item.text}
                  </Typography>
                </Stack>
              )}

              <Stack direction="row">
                {item.editable ? (
                  <Tooltip title="Save">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSave(index)}
                      disabled={!item.text.trim()}
                    >
                      <Save fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      sx={{ color: "text.secondary" }}
                      onClick={() => handleEdit(index)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    sx={{ color: "text.disabled" }}
                    onClick={() => handleRemove(index)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Button
          fullWidth
          variant="dashed" // Antaget att du har en custom-variant, annars använd 'outlined'
          startIcon={<Add />}
          onClick={handleAddRow}
          sx={{
            borderRadius: 2.5,
            py: 1,
            borderStyle: "dashed",
            fontWeight: 800,
            textTransform: "none",
          }}
        >
          Add Step
        </Button>
      </Stack>
    </Box>
  );
};

export default TankCleaningTable;
